import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  personaService as defaultPersonaService,
  type PersonaData,
  type PersonaStats,
} from '../services/personaService';
import type { PersonaService as PersonaServiceClient } from '../services/personaService';
import { DashboardContext } from './dashboardContext';
import type {
  DashboardContextValue,
  FavouritePersona,
  DashboardInvestor,
} from './dashboardTypes';
import { famousInvestors, ceo } from '../data/famousInvestors';

// Client-side favourite computation removed; we now use the backend API

function getInvestorKey(investor: DashboardInvestor): string {
  return investor.id ?? investor.name;
}

function mapFamousInvestorsToFallback(): DashboardInvestor[] {
  return famousInvestors.map((investor) => ({
    id: investor.name,
    name: investor.name,
    description: investor.description,
    photo: investor.photo ?? null,
    traits: {
      macroVsMicro: investor.traits.macroVsMicro,
      valueVsGrowth: investor.traits.valueVsGrowth,
      timeHorizon: investor.traits.timeHorizon,
      passiveVsActive: investor.traits.passiveVsActive,
      strategicVsFinancial: investor.traits.strategicVsFinancial,
    },
  }));
}

type DashboardProviderProps = {
  children: ReactNode;
  service?: PersonaServiceClient;
  favouriteService?: PersonaServiceClient;
};

export function DashboardProvider({
  children,
  service = defaultPersonaService,
  favouriteService,
}: DashboardProviderProps) {
  const investors = useMemo<DashboardInvestor[]>(() => mapFamousInvestorsToFallback(), []);
  const investorsLoading = false;
  const investorsError: string | null = null;
  const [selectedInvestor, setSelectedInvestor] = useState<DashboardInvestor | null>(
    () => investors[0] ?? null
  );

  const [personas, setPersonas] = useState<PersonaData[]>([]);
  const [personasLoading, setPersonasLoading] = useState(true);
  const [personaError, setPersonaError] = useState<string | null>(null);

  const [personaStats, setPersonaStats] = useState<PersonaStats | null>(null);
  const [personaStatsLoading, setPersonaStatsLoading] = useState(true);
  const [personaStatsError, setPersonaStatsError] = useState<string | null>(null);

  const [favourites, setFavourites] = useState<Map<string, FavouritePersona>>(new Map());
  const [favouriteError, setFavouriteError] = useState<string | null>(null);

  const [showInvestors, setShowInvestors] = useState(false);
  const [showSegmentColors, setShowSegmentColors] = useState(false);
  const [regeneratingFavouriteKeys, setRegeneratingFavouriteKeys] = useState<Set<string>>(new Set());
  const [autoFetchAttemptedKeys, setAutoFetchAttemptedKeys] = useState<Set<string>>(new Set());

  const favouriteClient = useMemo(
    () => favouriteService ?? service,
    [favouriteService, service]
  );

  const refreshPersonas = useCallback(async () => {
    setPersonasLoading(true);
    try {
      const response = await service.listPersonas({ limit: 1000 });
      setPersonas(response.personas);
      setPersonaError(null);
    } catch (error) {
      console.error('Failed to load personas', error);
      setPersonaError(
        error instanceof Error ? error.message : 'Failed to load personas'
      );
      setPersonas([]);
    } finally {
      setPersonasLoading(false);
    }
  }, [service]);

  const refreshPersonaStats = useCallback(async () => {
    setPersonaStatsLoading(true);
    try {
      const stats = await service.getStats();
      setPersonaStats(stats);
      setPersonaStatsError(null);
    } catch (error) {
      console.error('Failed to load persona stats', error);
      setPersonaStatsError(
        error instanceof Error ? error.message : 'Failed to load persona stats'
      );
      setPersonaStats(null);
    } finally {
      setPersonaStatsLoading(false);
    }
  }, [service]);

  const regenerateFavourite = useCallback(
    async (investor: DashboardInvestor | null) => {
      if (!investor) {
        setFavouriteError('No investor selected.');
        return;
      }

      const key = getInvestorKey(investor);
      setFavouriteError(null);
      setRegeneratingFavouriteKeys((current) => new Set(current).add(key));

      try {
        const payload = {
          name: investor.name,
          traits: investor.traits,
        } as const;

        const favourite = await favouriteClient.getFavouritePersonaForInvestor(payload);

        if (favourite) {
          setFavourites((current) => {
            const next = new Map(current);
            next.set(key, { persona: favourite, distance: 0 });
            return next;
          });
        } else {
          setFavourites((current) => {
            const next = new Map(current);
            next.delete(key);
            return next;
          });
          setFavouriteError('No favourite persona has been calculated yet.');
        }
      } catch (error) {
        console.error('Failed to fetch favourite persona from API', error);
        setFavouriteError(
          error instanceof Error ? error.message : 'Failed to fetch favourite persona'
        );
      } finally {
        setRegeneratingFavouriteKeys((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }

      // Fire-and-forget CEO favourite calculation to populate DB; ignore UI state
      try {
        if (ceo) {
          void favouriteClient.getFavouritePersonaForInvestor({
            name: ceo.name,
            traits: {
              macroVsMicro: ceo.traits.macroVsMicro,
              valueVsGrowth: ceo.traits.valueVsGrowth,
              timeHorizon: ceo.traits.timeHorizon,
              passiveVsActive: ceo.traits.passiveVsActive,
              strategicVsFinancial: ceo.traits.strategicVsFinancial,
            },
          });
        }
      } catch (e) {
        console.error('Failed to fetch CEOfavourite persona from API', e);
      }
    },
    [favouriteClient]
  );

  const markAutoFetchAttempted = useCallback((key: string) => {
    setAutoFetchAttemptedKeys((current) => {
      const next = new Set(current);
      next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    void refreshPersonas();
    void refreshPersonaStats();
  }, [refreshPersonaStats, refreshPersonas]);

  useEffect(() => {
    if (!selectedInvestor) {
      setFavouriteError(null);
    } else {
      // Clear error when investor changes; UI relies on favourites map and regenerating keys
      setFavouriteError(null);
    }
  }, [selectedInvestor]);

  const value = useMemo<DashboardContextValue>(
    () => ({
      personas,
      personasLoading,
      personaError,
      personaStats,
      personaStatsLoading,
      personaStatsError,
      refreshPersonas,
      refreshPersonaStats,
      investors,
      investorsLoading,
      investorsError,
      selectedInvestor,
      setSelectedInvestor,
      favourites,
      favouriteError,
      regenerateFavourite,
      regeneratingFavouriteKeys,
      autoFetchAttemptedKeys,
      markAutoFetchAttempted,
      showInvestors,
      setShowInvestors,
      showSegmentColors,
      setShowSegmentColors,
    }),
    [
      investors,
      investorsError,
      investorsLoading,
      selectedInvestor,
      favourites,
      favouriteError,
      personaError,
      personaStats,
      personaStatsError,
      personaStatsLoading,
      personas,
      personasLoading,
      refreshPersonaStats,
      refreshPersonas,
      regenerateFavourite,
      regeneratingFavouriteKeys,
      autoFetchAttemptedKeys,
      markAutoFetchAttempted,
      setSelectedInvestor,
      showInvestors,
      setShowInvestors,
      showSegmentColors,
      setShowSegmentColors,
    ]
  );

  return (
    <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>
  );
}
